import { Hero } from '@/features/Hero';
import ElasticAdapter from '@/features/shared/Adapters/ElasticAdapter';
import CMSWrapper from '@/features/shared/CMSWrapper';
import ContentContainer from '@/features/shared/ContentContainer';
import ElasticComponentContext, {
  ElasticComponentContextType,
} from '@/features/shared/ElasticComponent/ElasticComponentContext';
import { withGTM } from '@/features/shared/HOCs';
import { useBotSearchParams } from '@/features/shared/Hooks/useBotSearchParams';
import { useElasticSearch } from '@/features/shared/Hooks/useElasticSearch';
import { MuiBox, MuiContainer } from '@/features/shared/material-ui';
import {
  ColorNames,
  CUSTOM_FACETS,
  LoadingStatusValues,
  SpacingSizes,
  Theme,
} from '@/utils/constants';
import { readCsvLine } from '@/utils/helpers';
import { ContentListDocumentMapping } from '@/utils/mappings';
import {
  resolveBool,
  resolveImage,
  resolveLink,
  resolveRTC,
  resolveSortBy,
  resolveStatus,
  resolveTaxonomyFacets,
} from '@/utils/resolvers';
import { isEqual } from 'lodash';
import qs from 'query-string';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import SearchBar from './SearchBar/SearchBar';
import SolutionMarketplace from './SolutionMarketplace';
import {
  mapTarget,
  onDatalayer,
  onDatalayerCustomFilters,
  onDatalayerCustomSearch,
  onDatalayerCustomSort,
} from './SolutionMarketplace.datalayer';
import { withGTMPipe } from '@/features/shared/HOCs/withGTM';

const resolveCards = (obj: any) => (Object.keys(obj).length > 0 ? obj : null);

const resolveAnimate = ({ rawVal }: any) => {
  const { animatedHeadlineSegment: segments, staticHeadlineSegment } = rawVal || {};
  return {
    segments,
    staticHeadline: resolveRTC({ rawVal: staticHeadlineSegment }),
  };
};

const MappedSolutionMarketplace = memo(function MappedSolutionMarketplace({
  contentButtons,
  filter,
  featuredContent1,
  featuredContent2,
  featuredContent3,
  featuredContent4,
  featuredContent5,
  presentation,
  heroContent,
  pageModels,
  labels = {},
  config,
  sortBy: sortOptions = [],
}: any) {
  const { config: globalConfig, esLabels = {}, taxonomy } = pageModels;
  const { elasticsearch: elasticsearchConfig } = globalConfig || {};
  const { search_key: searchKey, engines_query_sorting_blacklist: sortingBlacklist } =
    elasticsearchConfig || {};
  const [ableToSearch, setAbleToSearch] = useState<boolean>(false);
  const { includeBreadcrumb, template } = presentation || {};
  const { taxonomyFacets, additionalFacets, links, linksTitle, searchLabel } = filter || {};
  const HAS_HERO = useMemo(() => /^hero-search-bar/i.test(template), [template]);
  const queryParams = useParams();
  const isFirstLoad = useRef(true);
  const pageHistory = useHistory();
  const {
    location: { pathname = '/' },
  } = pageHistory;
  const engines = (key: string) => {
    let engine = '';
    if (key === 'solutions') {
      engine = elasticsearchConfig.technology_solutions_engine;
    } else if (key === 'partners') {
      engine = elasticsearchConfig.technology_profile_engine;
    } else if (key === 'both') {
      engine = elasticsearchConfig.technology_partners_engine;
    } else {
      engine = elasticsearchConfig.technology_partners_engine;
    }
    return engine;
  };

  const facets: string[] = React.useMemo(
    () => [...(additionalFacets || []), ...(taxonomyFacets || [])],
    [additionalFacets, taxonomyFacets],
  );

  const defaultParams = useRef({
    sortBy: 'most-relevant',
    q: '',
    page: '1',
    keywords: '',
    filters: {},
    searchBy: 'both',
    size: '9',
  });

  useBotSearchParams();

  const {
    q = defaultParams.current.q,
    keywords = defaultParams.current.keywords,
    page = defaultParams.current.page,
    sortBy = defaultParams.current.sortBy,
    size = defaultParams.current.size,
    searchBy = defaultParams.current.searchBy,
    ...filters
  } = qs.parse(window.location.search);

  // Search Params
  const [search, setSearch] = useState({
    q,
    page,
    sortBy,
    keywords,
    filters: ElasticAdapter.encodeFilters(filters, [...facets, ...CUSTOM_FACETS]),
    searchBy,
    size,
  });

  const isThereValidParams = () => {
    const qparams = qs.parse(window.location.search);
    const VALID_PARAMS = Object.keys(defaultParams.current)
      .filter((key) => key !== 'filters')
      .filter((key) => key in qparams);

    return VALID_PARAMS.length > 0;
  };

  const [type, setType] = useState<'cms' | 'results'>(isThereValidParams() ? 'results' : 'cms');

  const resetParams = () => {
    setSearch(defaultParams.current);
    pageHistory.replace(pathname);
  };

  const engine = engines(search.searchBy as string);
  const isSortShown = useMemo(
    () => !(readCsvLine({ data: sortingBlacklist }).includes(engine) && search?.q),
    [engine, sortingBlacklist, search?.q],
  );

  const es = useElasticSearch({
    engine,
    searchParams: search,
    facets: [...facets, ...CUSTOM_FACETS],
    searchKey,
    labels: esLabels,
  });

  const setQueryParam = (key: string, value: any, isFilter?: boolean): void => {
    if (isFilter) {
      setSearch((data: any) => ({
        ...data,
        filters: {
          ...data.filters,
          [key]: value,
        },
        page: 1,
      }));
    } else {
      setSearch((data: any) => ({
        ...data,
        [key]: value,
      }));
    }
  };

  React.useEffect(() => {
    if (isSortShown) return;

    setQueryParam('sortBy', defaultParams.current.sortBy);
  }, [isSortShown]);

  // This effect patches anchor elements that are not listened by "setSearch".
  useEffect(() => {
    let isMounted = true;

    if (isFirstLoad.current) isFirstLoad.current = false;
    if (!isFirstLoad.current && isMounted && isThereValidParams()) setType('results');
    if (!isFirstLoad.current && isMounted && !isThereValidParams()) setType('cms');
    if (!isFirstLoad.current && isMounted) {
      setSearch({
        q,
        page,
        sortBy,
        keywords,
        filters: ElasticAdapter.encodeFilters(filters, [...facets, ...CUSTOM_FACETS]),
        searchBy,
        size,
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  useEffect(() => {
    if (!isEqual(defaultParams.current, search)) {
      setType('results');
      if (ableToSearch) {
        // eslint-disable-next-line no-restricted-globals
        history.pushState({}, '', `?${qs.stringify(ElasticAdapter.sanitizeParams(search))}`);
      }
    }

    setAbleToSearch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const heroContainerStyles = useMemo(
    () => ({
      transition: 'height 500ms ease-in-out',
      height: /cms/.test(type) ? '450px' : '0px',
    }),
    [type],
  );

  const allLabels = React.useMemo(
    () => ({ ...labels, search: searchLabel }),
    [labels, searchLabel],
  );

  const { loading } = LoadingStatusValues;
  const [status, setStatus] = useState<ElasticComponentContextType['status']>(loading);
  useEffect(() => resolveStatus({ rawVal: es, setStatus, enforceStatusUpdate: true }), [es]);
  const isCached = React.useMemo(() => es.isCached, [es.isCached]);
  const marketplacePresentation = React.useMemo(() => ({ isSortShown }), [isSortShown]);

  return (
    <div
      data-datalayer-position="solution_marketplace"
      data-datalayer-variant={presentation?.datalayerVariant}
    >
      <ElasticComponentContext.Provider
        value={{
          queryParams: search,
          updateQueryParams: setQueryParam,
          sortOptions,
          pagination: es.response.pagination,
          facets: es.facets,
          status,
          resetParams,
          labels: allLabels,
          callbackExternalFilters: () => setAbleToSearch(false),
          results: es.response.results,
          engine,
          requestID: es.response?.meta?.request_id,
          searchKey,
        }}
      >
        {HAS_HERO && (
          <div style={heroContainerStyles}>
            <Hero
              content={heroContent}
              contentButtons={contentButtons}
              presentation={{
                template: 'search',
                contentAlignment: 'center',
                backgroundOverlayOpacity: 25,
                containerBottomSpacing: SpacingSizes.none,
                containerBottomBackgroundColor: '',
                isBeneathNavigation: false,
                hasTextShadow: false,
                includeJumplinks: false,
                textColor: Theme.light,
                fullWidthContent: true,
              }}
            >
              <MuiBox pt={4} width="100%">
                <SearchBar size="large" displayInHero labels={{ ...labels, search: searchLabel }} />
              </MuiBox>
            </Hero>
          </div>
        )}

        <ContentContainer
          topSpacing={SpacingSizes.small}
          bottomSpacing={SpacingSizes.small}
          topBackgroundColor={ColorNames.eggshell}
          bottomBackgroundColor={ColorNames.eggshell}
        >
          <MuiContainer>
            <SolutionMarketplace
              type={type}
              labels={allLabels}
              config={config}
              linksTitle={linksTitle}
              links={links}
              contentButtons={contentButtons}
              includeBreadcrumb={includeBreadcrumb}
              blocks={[
                resolveCards(featuredContent1),
                resolveCards(featuredContent2),
                resolveCards(featuredContent3),
                resolveCards(featuredContent4),
                resolveCards(featuredContent5),
              ].filter((item) => !!item)}
              isCached={isCached}
              presentation={marketplacePresentation}
            />
          </MuiContainer>
        </ContentContainer>
      </ElasticComponentContext.Provider>
    </div>
  );
});

const resolveLinks = ({ rawVal, page, pageModels }: any) => {
  const rawLinks = Array.isArray(rawVal) ? rawVal : [rawVal];
  return rawLinks.map((item) => resolveLink({ rawVal: item, page, pageModels }));
};

const MappedSolutionMarketplaceGTM = withGTMPipe(
  {
    onDatalayer,
    config: { mapTarget },
  },
  {
    onDatalayer: onDatalayerCustomSort,
    config: {
      eventName: 'gtm:custom:sort',
      isCustom: true,
    },
  },
  {
    onDatalayer: onDatalayerCustomFilters,
    config: {
      eventName: 'gtm:custom:filters',
      isCustom: true,
    },
  },
  {
    onDatalayer: onDatalayerCustomSearch,
    config: {
      eventName: 'gtm:custom:search',
      isCustom: true,
    },
  },
)(MappedSolutionMarketplace);

const SolutionMarketplaceCMSWrapper = (brProps: any) => {
  const mapping = useRef({
    filterMenuDocument: [
      'filter',
      {
        taxonomyTopLevel: ['taxonomyFacets', resolveTaxonomyFacets],
        additionalfilters: 'additionalFacets',
        listTitle: 'linksTitle',
        listMenuLink: ['links', resolveLinks],
        searchFieldPlaceholder: 'searchLabel',
      },
    ],
    document: [
      'heroContent',
      {
        logo: [null, resolveImage],
        headline: [null, resolveRTC],
        subHeading: ['subheading', resolveRTC],
        primaryCTA: [null, resolveLink],
        secondaryCTA: [null, resolveLink],
        backgroundImage: [null, resolveImage],
        animatedHeading: [null, resolveAnimate],
      },
    ],
    'marketplace.labels': 'labels',
    'marketplace.config': 'config',
    featuredContent1Document: ['featuredContent1', ContentListDocumentMapping],
    featuredContent2Document: ['featuredContent2', ContentListDocumentMapping],
    featuredContent3Document: ['featuredContent3', ContentListDocumentMapping],
    featuredContent4Document: ['featuredContent4', ContentListDocumentMapping],
    featuredContent5Document: ['featuredContent5', ContentListDocumentMapping],
    componentParameterMap: [
      'presentation',
      {
        template: null,
        includeBreadcrumb: ['includeBreadcrumb', resolveBool],
        'variant-label': 'datalayerVariant',
      },
    ],
    'value-lists/marketplace-sort-by': ['sortBy', resolveSortBy],
  });

  const requiredFields = useRef({
    filterMenuDocument: '<b>Filter Menu</b> required',
  });

  const contentButtonNames = useRef({
    filterMenuDocument: 'filter',
    document: 'content',
    featuredContent1Document: 'featuredContent1',
    featuredContent2Document: 'featuredContent2',
    featuredContent3Document: 'featuredContent3',
    featuredContent4Document: 'featuredContent4',
    featuredContent5Document: 'featuredContent5',
  });

  return (
    <CMSWrapper
      brProps={brProps}
      mapping={mapping.current}
      render={MappedSolutionMarketplaceGTM}
      requiredFields={requiredFields.current}
      contentButtonNames={contentButtonNames.current}
    />
  );
};

export default SolutionMarketplaceCMSWrapper;
